import React from 'react'
import { motion, Variants, MotionStyle } from 'framer-motion'

// ──────────────────────────────────────────────
// Shared easing curve
// ──────────────────────────────────────────────
const smoothEase: [number, number, number, number] = [0.25, 0.46, 0.45, 0.94]

// ──────────────────────────────────────────────
// Reusable animation variants
// ──────────────────────────────────────────────

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: smoothEase },
  },
}

export const fadeInDown: Variants = {
  hidden: { opacity: 0, y: -40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: smoothEase },
  },
}

export const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: smoothEase },
  },
}

export const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.7, ease: smoothEase },
  },
}

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: smoothEase },
  },
}

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6, ease: smoothEase },
  },
}

export const scaleUp: Variants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.8, ease: smoothEase },
  },
}

export const staggerContainer: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
}

export const staggerContainerFast: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
}

export const floatUp: Variants = {
  hidden: { opacity: 0, y: 80, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, ease: smoothEase },
  },
}

// ──────────────────────────────────────────────
// Animated wrapper components
// ──────────────────────────────────────────────

interface AnimatedSectionProps {
  children: React.ReactNode
  variants?: Variants
  className?: string
  style?: MotionStyle
  delay?: number
  threshold?: number
  as?: React.ElementType
}

/**
 * A wrapper that triggers an animation when the element scrolls into view.
 * Uses Framer Motion's `whileInView` for clean scroll-triggered animations.
 */
export const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  children,
  variants = fadeInUp,
  className,
  style,
  delay = 0,
  threshold = 0.15,
}) => {
  // Extract transition from variants if it exists to avoid overriding it with just delay
  const variantTransition =
    (variants.visible as { transition?: object })?.transition || {}

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: threshold }}
      variants={variants}
      className={className}
      style={style}
      transition={{
        ...variantTransition,
        ...(delay ? { delay } : {}),
      }}
    >
      {children}
    </motion.div>
  )
}

/**
 * A container that staggers its children's animations.
 * Wrap child elements with <motion.div variants={fadeInUp}> etc.
 */
export const StaggerContainer: React.FC<AnimatedSectionProps> = ({
  children,
  variants = staggerContainer,
  className,
  style,
  threshold = 0.1,
}) => {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: threshold }}
      variants={variants}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  )
}

/**
 * A simple child item that inherits parent stagger timing.
 */
interface StaggerItemProps {
  children: React.ReactNode
  variants?: Variants
  className?: string
  style?: MotionStyle
}

export const StaggerItem: React.FC<StaggerItemProps> = ({
  children,
  variants = fadeInUp,
  className,
  style,
}) => {
  return (
    <motion.div variants={variants} className={className} style={style}>
      {children}
    </motion.div>
  )
}

export { smoothEase }
export default AnimatedSection
